import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

// Every plain label tooltip in the app renders through here: 12px gray-100 text
// on gray-800, gray-600 border, px-2/py-1.
const TOOLTIP_CLASS = 'px-2 py-1 bg-gray-800 border border-gray-600 text-gray-100 rounded shadow-lg text-xs whitespace-nowrap';

const Tooltip = ({
    content,
    children,
    offset = 8,
    verticalOffset = -20,
    className = "",
    parentClass = "",
    position = "right",
    disabled = false,
    hoverDelay = 0,
    interactive = false
}) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const wrapperRef = useRef(null);
    const tooltipRef = useRef(null);
    const rafRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    // An interactive tooltip has to survive the gap between the trigger and the
    // panel, so leaving the trigger only schedules the close — entering the panel
    // cancels it.
    const closeTimeoutRef = useRef(null);

    const updatePosition = () => {
        if (!wrapperRef.current || !tooltipRef.current) return;

        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        let top;
        let left;

        if (position === 'top' || position === 'top-start') {
            top = wrapperRect.top + window.scrollY - tooltipRect.height - offset;
            // "top-start" aligns the tooltip's left edge to the wrapper's left
            // (start of the text); "top" centers it over the wrapper.
            left = position === 'top-start'
                ? wrapperRect.left + window.scrollX
                : wrapperRect.left + window.scrollX + (wrapperRect.width / 2) - (tooltipRect.width / 2);

            // keep the tooltip within the horizontal viewport so it is never clipped
            const maxLeft = window.scrollX + window.innerWidth - tooltipRect.width - offset;
            const minLeft = window.scrollX + offset;
            if (left > maxLeft) left = maxLeft;
            if (left < minLeft) left = minLeft;
        } else {
            top =
                wrapperRect.top +
                wrapperRect.height / 2 -
                tooltipRect.height / 2 +
                window.scrollY +
                verticalOffset;
            const fitsRight =
                wrapperRect.right + tooltipRect.width + offset < window.innerWidth;

            if (fitsRight) {
                left = wrapperRect.right + window.scrollX + offset;
            } else {
                left = wrapperRect.left - tooltipRect.width + window.scrollX - offset;
            }
        }

        const viewportTop = window.scrollY;
        const viewportBottom = window.scrollY + window.innerHeight;

        if (top < viewportTop) top = viewportTop + offset;
        if (top + tooltipRect.height > viewportBottom) {
            top = viewportBottom - tooltipRect.height - offset;
        }

        setCoords({ top, left });
    };

    const scheduleUpdate = () => {
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            updatePosition();
        });
    };

    useEffect(() => {
        if (!visible) return;

        updatePosition();
        window.addEventListener("resize", scheduleUpdate);
        window.addEventListener("scroll", scheduleUpdate, true);

        return () => {
            window.removeEventListener("resize", scheduleUpdate);
            window.removeEventListener("scroll", scheduleUpdate, true);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [visible]);

    useEffect(() => () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    const cancelClose = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const handleMouseEnter = () => {
        if (disabled) return;
        cancelClose();
        if (!hoverDelay) {
            setVisible(true);
            return;
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setVisible(true);
            hoverTimeoutRef.current = null;
        }, hoverDelay);
    };

    const hideTooltip = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        cancelClose();
        setVisible(false);
    };

    // Leaving the trigger of an interactive tooltip gives the pointer time to
    // reach the panel; a plain tooltip closes at once, as it always has.
    const handleMouseLeave = () => {
        if (!interactive) {
            hideTooltip();
            return;
        }
        cancelClose();
        closeTimeoutRef.current = setTimeout(() => {
            closeTimeoutRef.current = null;
            setVisible(false);
        }, 150);
    };

    return (
        <span
            ref={wrapperRef}
            className={`inline-flex ${parentClass}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onContextMenu={hideTooltip}
            onFocus={() => setVisible(true)}
            onBlur={hideTooltip}
            tabIndex={0}
        >
            {children}

            {visible && content && !disabled &&
                ReactDOM.createPortal(
                    <div
                        ref={tooltipRef}
                        role="tooltip"
                        style={{
                            position: "absolute",
                            top: coords.top,
                            left: coords.left,
                            zIndex: 99999,
                            // 🔑 prevents flicker — an interactive panel opts in to
                            // pointer events so its own controls can be clicked.
                            pointerEvents: interactive ? 'auto' : 'none',
                        }}
                        onMouseEnter={interactive ? cancelClose : undefined}
                        onMouseLeave={interactive ? handleMouseLeave : undefined}
                        className={`${TOOLTIP_CLASS} ${className}`}
                    >
                        {content}
                    </div>,
                    document.body
                )}
        </span>
    );
};

export default Tooltip;
